import {useMemo} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {V3Page} from '@/pages/components/audit-program/v-section/constants';
import {V3_PAGE} from '@/pages/components/audit-program/v-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface V3EstimatesViewProps {
    header: AuditProgramSectionVO;
    page: V3Page;
    refCode?: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {...cellBorder, fontWeight: 600, fontSize: '0.7rem', py: 0.6, px: 0.35};
const bodySx = {...cellBorder, py: 0.35, px: 0.3, verticalAlign: 'top' as const, overflow: 'hidden'};
const fieldSx = {minWidth: 0, width: '100%', maxWidth: '100%'};

/** V3 — Review of accounting estimates for bias. */
export default function V3EstimatesView({
    header,
    page,
    refCode = 'V3',
    values,
    onChange,
}: V3EstimatesViewProps) {
    const meta = {
        title: page.title || V3_PAGE.title,
        blankRows: page.blankRows || V3_PAGE.blankRows,
        columns: page.columns?.length ? page.columns : V3_PAGE.columns,
    };
    const rowIds = useMemo(
        () => Array.from({length: Math.max(1, meta.blankRows)}, (_, i) => `e${i + 1}`),
        [meta.blankRows],
    );

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 1.5, mt: 0.5}}
            >
                {meta.title}
            </Typography>

            <TableContainer sx={{mb: 2, overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 960, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} style={{width: 32}}/>
                            {meta.columns.map((c) => (
                                <TableCell key={c.id} sx={headSx}>{c.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rowIds.map((id, idx) => (
                            <TableRow key={id}>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2" color="text.secondary">{idx + 1}</Typography>
                                </TableCell>
                                {meta.columns.map((c) => (
                                    <TableCell key={c.id} sx={bodySx}>
                                        <InputField
                                            type="text"
                                            fullWidth
                                            compact
                                            align={c.id === 'estimate' || c.id === 'comments' ? 'left' : 'right'}
                                            sx={fieldSx}
                                            value={values[`${id}.${c.id}`] ?? ''}
                                            onChange={(e) => onChange(`${id}.${c.id}`, e.target.value)}
                                        />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
